import {BaseComponent} from '../base/component.base.js';
import template from './billing-card.template.html';
import styles from './billing-card.styles.css';
import {toInvoiceCostRequest, validateBillingItemForm} from '../../core/billing/billing.core.js';
import {confirmDiscard} from '../confirm-dialog/confirm-discard.js';
import {renderBillingCard} from './billing-card.view.js';
import {renderBillingHistory} from './billing-history.view.js';
import {
    billingFormSnapshot,
    connectIndexationCalculator,
    fillBillingForm,
    readBillingForm,
    showBillingFormErrors,
    showPaidUntil,
    withBillingItemFields,
} from '../billing-item-form/billing-item-form.view.js';

/**
 * The recurring billing card of a client: its items, and the add/edit form. It owns the items, the
 * open form and the form's unsaved-changes state, and only emits events; the driving adapter
 * talks to the API and hands the items in through `items`.
 *
 * Events (bubbling, composed): `billing-save {slug, id | null, request}` and
 * `billing-delete {id, label}`. It also emits `billing-invoice-request {item}` (open the
 * mark-invoiced dialog), `billing-history-request {id}` (the adapter answers with `showHistory` or
 * `showHistoryError`, and is asked again whenever `items` is set while the panel is open) and
 * `billing-invoice-undo {itemId, invoiceId}` (after the user confirmed) and
 * `billing-invoice-cost {itemId, invoiceId, request}` (a corrected invoice cost). The form's indexation
 * calculator emits `clipboard-copy {text, label}` to copy its invoice line.
 */
export class BillingCardComponent extends BaseComponent {
    /** Slug of the client the card shows, sent with `billing-save`. */
    slug = '';
    /** @type {import('../../shell/types/billing.types.js').BillingItemApiResponse[] | null} null until loaded */
    #items = null;
    /** The item open in the form (`item` is null for a new one); null while the form is closed. */
    #editing = null;
    /** Form values as last opened; see `hasUnsavedChanges`. */
    #savedSnapshot = null;
    /** Ids of the items whose history panel is open. */
    #expanded = new Set();
    /** Last answer per item id: `{invoices}`, `{error}`, or null while loading. */
    #histories = new Map();
    /** The invoice whose cost is being corrected, `{itemId, invoiceId}`; null when none is. */
    #costEditing = null;
    /** The invoice whose Edit cost button gets the focus once the history answers after a saved cost. */
    #costFocus = null;

    connectedCallback() {
        const {html, css} = withBillingItemFields(template, styles);
        this.loadTemplate(html, css);
        this.$('#billing-form').addEventListener('submit', (e) => this.#handleSubmit(e));
        connectIndexationCalculator(this.$('#billing-form'), (name, detail) => this.emit(name, detail));
        this.shadowRoot.addEventListener('click', (e) => this.#handleClick(e));
        this.shadowRoot.addEventListener('submit', (e) => this.#handleCostSubmit(e));
    }

    /**
     * Shows the client's items. An item that was open in the form and is gone from `items`
     * (deleted) closes the form.
     *
     * @param {import('../../shell/types/billing.types.js').BillingItemApiResponse[]} items
     */
    set items(items) {
        this.#items = items;
        this.$('#billing-load-error').hidden = true;
        const editing = this.#editing?.item;
        const current = editing && items.find((item) => item.id === editing.id);
        if (editing && !current) {
            this.closeForm();
            return;
        }
        if (current && (current.paidUntil !== editing.paidUntil || current.lastInvoice?.id !== editing.lastInvoice?.id)) {
            this.#refreshOpenForm(current);
        } else if (current) {
            this.#editing = {item: current};
        }
        this.#render();
        for (const id of this.#expanded) {
            this.emit('billing-history-request', {id});
        }
    }

    /**
     * Shows the invoices of an item whose history panel is open.
     *
     * @param {number} id
     * @param {import('../../shell/types/billing.types.js').BillingInvoiceApiResponse[]} invoices newest first
     */
    showHistory(id, invoices) {
        this.#setHistory(id, {invoices});
    }

    /**
     * @param {number} id
     * @param {string} message why the invoices could not be loaded or the latest one not undone
     */
    showHistoryError(id, message) {
        this.#setHistory(id, {error: message});
    }

    /**
     * Forgets the items, the open form and any error, for when another client is shown.
     *
     * @param {string} slug slug of the client that is shown next
     */
    reset(slug) {
        this.slug = slug;
        this.#items = null;
        this.#expanded = new Set();
        this.#histories = new Map();
        this.#costEditing = null;
        this.#costFocus = null;
        this.$('#billing-load-error').hidden = true;
        this.closeForm();
    }

    /** @param {string} message why the items could not be loaded; shown instead of the list */
    showLoadError(message) {
        this.#items = null;
        this.#render();
        const error = this.$('#billing-load-error');
        error.textContent = message;
        error.hidden = false;
    }

    /**
     * Shows why the backend rejected the item, in the form where it gets fixed.
     *
     * @param {string | null} message null hides the error
     * @returns {boolean} true when the form is open and shows the message
     */
    showFormError(message) {
        const error = this.$('#billing-form-error');
        error.textContent = message || '';
        error.hidden = !message;
        return this.#editing !== null;
    }

    /** Closes the form and forgets what was typed in it. */
    closeForm() {
        this.#editing = null;
        this.#savedSnapshot = null;
        this.$('#billing-form').hidden = true;
        this.showFormError(null);
        this.#render();
    }

    /** @param {boolean} value disables the save button while a request runs, so it cannot be sent twice */
    set submitting(value) {
        this.$('#btn-submit-billing').disabled = value;
    }

    /** @returns {boolean} true when the open form differs from the item as last opened, or an open cost input from its invoice */
    hasUnsavedChanges() {
        return this.#formHasUnsavedChanges() || this.#costHasUnsavedChanges();
    }

    /**
     * Asks whether to drop the unsaved changes of the form and of the cost input; both are left as they are.
     *
     * @returns {Promise<boolean>} true when the user chose to discard every one
     */
    async confirmDiscard() {
        if (this.#formHasUnsavedChanges() && !await this.#confirmDiscardForm()) {
            return false;
        }
        return !this.#costHasUnsavedChanges() || confirmDiscard(
            'Discard the invoice cost?',
            'The cost you typed for this invoice is not saved and will be lost.',
        );
    }

    /** Closes the form and the cost input, dropping what was typed in them. */
    discardChanges() {
        this.closeForm();
        this.#editCost(null);
    }

    #formHasUnsavedChanges() {
        return this.#editing !== null && billingFormSnapshot(this.$('#billing-form')) !== this.#savedSnapshot;
    }

    #costHasUnsavedChanges() {
        const input = this.$('.history-cost-input');
        return input !== null && input.value !== input.defaultValue;
    }

    #confirmDiscardForm() {
        const label = this.#editing.item?.label;
        return confirmDiscard(
            label ? `Discard changes to "${label}"?` : 'Discard the new billing item?',
            label
                ? 'The changes you made to this billing item are not saved and will be lost.'
                : 'The billing item you started is not saved and will be lost.',
        );
    }

    /**
     * An invoice recorded or undone changed the paid-until date of the item open in the form. A
     * form without changes closes; a form with changes stays, gets the new date and lock, and
     * keeps what was typed.
     */
    #refreshOpenForm(item) {
        if (!this.#formHasUnsavedChanges()) {
            this.closeForm();
            return;
        }
        const form = this.$('#billing-form');
        showPaidUntil(form, item);
        this.#editing = {item};
        this.#savedSnapshot = JSON.stringify({...JSON.parse(this.#savedSnapshot), paidUntil: item.paidUntil});
    }

    /**
     * A history answer replaces the panel. A cost correction open in it closes once its cost is saved or its
     * invoice is gone, and otherwise stays open with what was typed.
     */
    #setHistory(id, state) {
        if (!this.#expanded.has(id)) {
            return;
        }
        this.#histories.set(id, state);
        const editing = this.#costEditing?.itemId === id ? this.#costEditing : null;
        if (editing && (this.#costFocus !== null || !state.invoices?.some((invoice) => invoice.id === editing.invoiceId))) {
            this.#costEditing = null;
        }
        this.#renderHistory(id);
        if (this.#costFocus?.itemId === id) {
            this.#focusCostEdit(this.#costFocus.invoiceId);
        }
    }

    /** Focuses the Edit cost button of an invoice, when its row is shown. */
    #focusCostEdit(invoiceId) {
        this.#costFocus = null;
        this.$(`.btn-history-cost-edit[data-invoice-id="${invoiceId}"]`)?.focus();
    }

    /** @param {{invoiceId: number, value: string} | null} [typed] what the open cost input held before a re-render */
    #renderHistory(id, typed = this.#typedCost()) {
        const panel = this.$(`.billing-item-extra[data-id="${id}"]`);
        if (panel) {
            const editing = this.#costEditing?.itemId === id ? this.#costEditing.invoiceId : null;
            renderBillingHistory(panel, this.#histories.get(id) ?? null, editing);
            const input = panel.querySelector('.history-cost-input');
            if (input && typed?.invoiceId === editing) {
                input.value = typed.value;
            }
        }
    }

    /** @returns {{invoiceId: number, value: string} | null} the invoice and value of the open cost input */
    #typedCost() {
        const form = this.$('.history-cost-form');
        return form && {invoiceId: Number(form.dataset.invoiceId), value: form.querySelector('.history-cost-input').value};
    }

    /** @param {{itemId: number, invoiceId: number} | null} editing */
    #editCost(editing) {
        const previous = this.#costEditing?.itemId;
        this.#costEditing = editing;
        this.#costFocus = null;
        if (previous !== undefined && previous !== editing?.itemId) {
            this.#renderHistory(previous);
        }
        if (editing) {
            this.#renderHistory(editing.itemId);
            this.$('.history-cost-input').focus();
        }
    }

    #handleCostSubmit(e) {
        const form = e.target.closest('.history-cost-form');
        if (!form) {
            return;
        }
        e.preventDefault();
        const input = form.querySelector('.history-cost-input');
        const {error, request} = toInvoiceCostRequest(input.value);
        const message = form.querySelector('.history-cost-error');
        message.textContent = error ?? '';
        message.hidden = !error;
        input.setAttribute('aria-invalid', String(Boolean(error)));
        if (request) {
            this.#costFocus = this.#costEditing;
            this.emit('billing-invoice-cost', {...this.#costEditing, request});
        }
    }

    /** @param {import('../../shell/types/billing.types.js').BillingItemApiResponse | null} item null opens the form for a new item */
    #open(item) {
        const form = this.$('#billing-form');
        this.#editing = {item};
        fillBillingForm(form, item);
        this.showFormError(null);
        this.$('#billing-form-title').textContent = item ? `Edit billing item: ${item.label}` : 'Add billing item';
        form.hidden = false;
        this.#savedSnapshot = billingFormSnapshot(form);
        this.#render();
        this.$('#billing-label').focus();
    }

    #render() {
        const list = this.$('#billing-list');
        if (this.#items === null) {
            list.innerHTML = '';
            return;
        }
        const typed = this.#typedCost();
        renderBillingCard(list, this.#items, {editingId: this.#editing?.item?.id ?? null, expandedIds: this.#expanded});
        for (const id of this.#expanded) {
            this.#renderHistory(id, typed);
        }
    }

    #toggleHistory(id) {
        if (this.#expanded.delete(id)) {
            this.#histories.delete(id);
            this.#render();
            return;
        }
        this.#expanded.add(id);
        this.#render();
        this.emit('billing-history-request', {id});
    }

    async #undo(itemId, invoiceId) {
        const invoice = this.#histories.get(itemId).invoices.find((candidate) => candidate.id === invoiceId);
        const confirmed = await document.querySelector('admin-confirm-dialog').ask({
            title: `Undo invoice ${invoice.invoiceNumber}?`,
            body: 'The invoice is removed, its amount no longer counts as income and the item is paid until the start of its period again.',
            confirmLabel: 'Undo invoice',
            cancelLabel: 'Cancel',
            danger: true,
        });
        if (confirmed) {
            this.emit('billing-invoice-undo', {itemId, invoiceId});
        }
    }

    async #handleClick(e) {
        if (e.target.closest('.btn-billing-add') && await this.#mayReplaceForm()) {
            this.#open(null);
        }
        const btnEdit = e.target.closest('.btn-billing-edit');
        if (btnEdit && await this.#mayReplaceForm()) {
            this.#open(this.#items.find((item) => item.id === Number(btnEdit.dataset.id)));
        }
        const btnInvoiced = e.target.closest('.btn-billing-invoiced');
        if (btnInvoiced) {
            this.emit('billing-invoice-request', {item: this.#items.find((item) => item.id === Number(btnInvoiced.dataset.id))});
        }
        const btnHistory = e.target.closest('.btn-billing-history');
        if (btnHistory) {
            this.#toggleHistory(Number(btnHistory.dataset.id));
        }
        const btnCostEdit = e.target.closest('.btn-history-cost-edit');
        if (btnCostEdit) {
            this.#editCost({itemId: Number(btnCostEdit.closest('.billing-item').dataset.id), invoiceId: Number(btnCostEdit.dataset.invoiceId)});
        }
        if (e.target.closest('.btn-history-cost-cancel')) {
            const {invoiceId} = this.#costEditing;
            this.#editCost(null);
            this.#focusCostEdit(invoiceId);
        }
        const btnUndo = e.target.closest('.btn-billing-undo');
        if (btnUndo) {
            await this.#undo(Number(btnUndo.closest('.billing-item').dataset.id), Number(btnUndo.dataset.invoiceId));
        }
        const btnDelete = e.target.closest('.btn-billing-delete');
        if (btnDelete) {
            const item = this.#items.find((candidate) => candidate.id === Number(btnDelete.dataset.id));
            const confirmed = await document.querySelector('admin-confirm-dialog').ask({
                title: `Delete billing item "${item.label}"?`,
                body: 'The item and its invoice history are removed. Income already recorded is no longer counted.',
                confirmLabel: 'Delete item',
                cancelLabel: 'Cancel',
                danger: true,
            });
            if (confirmed) {
                this.emit('billing-delete', {id: item.id, label: item.label});
            }
        }
        if (e.target.closest('#btn-cancel-billing') && await this.#mayReplaceForm()) {
            this.closeForm();
        }
    }

    /** @returns {Promise<boolean>} true when the form has no unsaved changes or they may be dropped */
    async #mayReplaceForm() {
        return !this.#formHasUnsavedChanges() || this.#confirmDiscardForm();
    }

    #handleSubmit(e) {
        e.preventDefault();
        this.showFormError(null);
        const form = this.$('#billing-form');
        const {valid, errors, request} = validateBillingItemForm(readBillingForm(form));
        showBillingFormErrors(form, errors);
        if (valid) {
            this.emit('billing-save', {slug: this.slug, id: this.#editing.item?.id ?? null, request});
        }
    }
}

customElements.define('billing-card', BillingCardComponent);
